import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Building2, Search, DollarSign, TrendingDown, TrendingUp, CalendarDays,
  Package, ChevronDown, ChevronUp, Store, Clock, ArrowUpDown,
  BarChart3, Loader2, Receipt, User, Phone, Mail, FileText, Pencil, Check, X,
  Trash2, RefreshCw
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CategoryBadge({ category }) {
  const labels = {
    rent: "Rent", utilities: "Utilities", salaries: "Salaries", supplies: "Supplies",
    marketing: "Marketing", insurance: "Insurance", maintenance: "Maintenance",
    equipment: "Equipment", food_cost: "Food Cost", beverage_cost: "Beverage Cost",
    software: "Software", professional_services: "Professional Services",
    other_expense: "Other", other_income: "Other Income",
  };
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200/60">
      {labels[category] || category}
    </span>
  );
}

function ProviderCard({ provider, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact_name: provider.contact_name || "",
    contact_phone: provider.contact_phone || "",
    contact_email: provider.contact_email || "",
    contract_renewal_date: provider.contract_renewal_date || "",
    notes: provider.notes || "",
  });

  const hasData = provider.transaction_count > 0;
  const hasContact = provider.contact_name || provider.contact_phone || provider.contact_email || provider.contract_renewal_date || provider.notes;
  const lastDate = provider.last_transaction_date
    ? new Date(provider.last_transaction_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const renewalDate = provider.contract_renewal_date
    ? new Date(provider.contract_renewal_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const isRenewalSoon = provider.contract_renewal_date && 
    new Date(provider.contract_renewal_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  // Net position: income - spend
  const netPosition = provider.total_income - provider.total_spend;
  const isNetPositive = netPosition >= 0;
  const isSupplier = provider.total_spend > provider.total_income;
  const isCustomer = provider.total_income > provider.total_spend;
  const providerType = isCustomer ? "Customer / Investor" : "Supplier";
  const typeColor = isCustomer ? "text-emerald-600 bg-emerald-50 border-emerald-200" : "text-stone-600 bg-stone-50 border-stone-200";

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== (provider[k] || "")) updates[k] = v || null;
      }
      if (Object.keys(updates).length === 0) { setEditing(false); setSaving(false); return; }
      await axios.put(`${API}/providers/${provider.id}`, updates);
      toast.success("Provider updated");
      onUpdate();
      setEditing(false);
    } catch (e) { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid={`provider-card-${provider.id}`}
      className="border-stone-200/60 hover:shadow-sm transition-shadow">
      <CardContent className="p-0">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full text-left p-4 flex items-center gap-4"
          data-testid={`provider-toggle-${provider.id}`}
        >
          <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-stone-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-stone-900 truncate">{provider.name}</p>
              {hasData && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${typeColor}`}>{providerType}</span>}
              {hasContact && <User className="w-3 h-3 text-stone-400" />}
              {isRenewalSoon && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Renewal soon</span>}
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {hasData ? (
                <>
                  <span className="text-xs text-stone-500 flex items-center gap-1">
                    <Receipt className="w-3 h-3" /> {provider.transaction_count} transactions
                  </span>
                  {lastDate && (
                    <span className="text-xs text-stone-500 flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" /> Last: {lastDate}
                    </span>
                  )}
                  {provider.stores?.length > 0 && (
                    <span className="text-xs text-stone-500 flex items-center gap-1">
                      <Store className="w-3 h-3" /> {provider.stores.join(", ")}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-stone-400">No transactions yet</span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className={`text-sm font-bold ${isNetPositive ? "text-emerald-600" : "text-stone-900"}`}>
              {isNetPositive && netPosition > 0 ? "+" : ""}{fmt(Math.abs(netPosition))}
            </p>
            <p className="text-[11px] text-stone-400">net {isNetPositive ? "income" : "spend"}</p>
            {(provider.total_spend > 0 && provider.total_income > 0) && (
              <p className="text-[10px] text-stone-400">
                {fmt(provider.total_income)} in / {fmt(provider.total_spend)} out
              </p>
            )}
          </div>
          {provider.upcoming_payments > 0 && (
            <div className="text-right flex-shrink-0 pl-3 border-l border-stone-100">
              <p className="text-sm font-semibold text-amber-600">{fmt(provider.upcoming_total)}</p>
              <p className="text-[11px] text-amber-500">{provider.upcoming_payments} upcoming</p>
            </div>
          )}
          <div className="flex-shrink-0 ml-1">
            {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          </div>
        </button>

        {expanded && (
          <div className="px-4 pb-4 border-t border-stone-100 space-y-4">
            {/* Contact & Details */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-stone-600">Contact & Details</p>
                <div className="flex items-center gap-2">
                  {!editing && (
                    <button data-testid={`provider-edit-${provider.id}`}
                      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                      className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  )}
                  {!hasData && !editing && (
                    <button data-testid={`provider-delete-${provider.id}`}
                      onClick={(e) => { e.stopPropagation(); onDelete(provider.id, provider.name); }}
                      className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>

              {editing ? (
                <div className="space-y-3 bg-stone-50 rounded-lg p-3" onClick={e => e.stopPropagation()}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-stone-500 font-medium mb-1 block">Account Manager</label>
                      <Input data-testid={`provider-contact-name-${provider.id}`}
                        value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                        placeholder="e.g. John Smith" className="text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-[11px] text-stone-500 font-medium mb-1 block">Phone</label>
                      <Input data-testid={`provider-contact-phone-${provider.id}`}
                        value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                        placeholder="e.g. +1 555-0123" className="text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-[11px] text-stone-500 font-medium mb-1 block">Email</label>
                      <Input data-testid={`provider-contact-email-${provider.id}`}
                        value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                        placeholder="e.g. john@acme.com" className="text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-[11px] text-stone-500 font-medium mb-1 block">Contract Renewal Date</label>
                      <Input data-testid={`provider-renewal-${provider.id}`}
                        type="date" value={form.contract_renewal_date}
                        onChange={e => setForm(f => ({ ...f, contract_renewal_date: e.target.value }))}
                        className="text-sm h-9" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-stone-500 font-medium mb-1 block">Notes</label>
                    <Textarea data-testid={`provider-notes-${provider.id}`}
                      value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Contract terms, special pricing, key contacts..."
                      className="text-sm min-h-[60px]" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(false); setForm({
                      contact_name: provider.contact_name || "", contact_phone: provider.contact_phone || "",
                      contact_email: provider.contact_email || "", contract_renewal_date: provider.contract_renewal_date || "",
                      notes: provider.notes || "",
                    }); }} className="h-8 text-xs">
                      <X className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 text-xs bg-stone-900 text-white hover:bg-stone-800"
                      data-testid={`provider-save-${provider.id}`}>
                      {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Save
                    </Button>
                  </div>
                </div>
              ) : hasContact ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-stone-50 rounded-lg p-3">
                  {provider.contact_name && (
                    <div className="flex items-center gap-2 text-xs text-stone-600">
                      <User className="w-3.5 h-3.5 text-stone-400" /> {provider.contact_name}
                    </div>
                  )}
                  {provider.contact_phone && (
                    <div className="flex items-center gap-2 text-xs text-stone-600">
                      <Phone className="w-3.5 h-3.5 text-stone-400" /> {provider.contact_phone}
                    </div>
                  )}
                  {provider.contact_email && (
                    <div className="flex items-center gap-2 text-xs text-stone-600">
                      <Mail className="w-3.5 h-3.5 text-stone-400" /> {provider.contact_email}
                    </div>
                  )}
                  {renewalDate && (
                    <div className={`flex items-center gap-2 text-xs ${isRenewalSoon ? "text-amber-600 font-medium" : "text-stone-600"}`}>
                      <CalendarDays className="w-3.5 h-3.5 text-stone-400" /> Renewal: {renewalDate}
                    </div>
                  )}
                  {provider.notes && (
                    <div className="col-span-full mt-1 flex items-start gap-2 text-xs text-stone-600">
                      <FileText className="w-3.5 h-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
                      <p className="whitespace-pre-wrap">{provider.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-stone-400 italic">No contact info yet — click Edit to add details</p>
              )}
            </div>

            {/* Category Breakdown */}
            {provider.categories?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-2">Breakdown by Category</p>
                <div className="space-y-2">
                  {provider.categories.map(cat => {
                    const catTotal = cat.spend + cat.income;
                    const maxAmount = Math.max(provider.total_spend, provider.total_income) || 1;
                    const pct = (catTotal / maxAmount) * 100;
                    const isIncome = cat.income > cat.spend;
                    return (
                      <div key={cat.category} className="flex items-center gap-3">
                        <CategoryBadge category={cat.category} />
                        <div className="flex-1 bg-stone-100 rounded-full h-2 overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${isIncome ? "bg-emerald-500" : "bg-stone-700"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <span className={`text-xs font-medium w-24 text-right ${isIncome ? "text-emerald-600" : "text-stone-700"}`}>
                          {isIncome ? "+" : ""}{fmt(isIncome ? cat.income : cat.spend)}
                        </span>
                        <span className="text-[10px] text-stone-400 w-12 text-right">{cat.count} txns</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProviderDirectory() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("spend");

  const fetchProviders = async () => {
    try {
      const res = await axios.get(`${API}/providers/directory`);
      setProviders(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load providers");
    } finally {
      setLoading(false);
    }
  };

  const syncProviders = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/providers/sync`);
      const { created, total } = res.data;
      if (created > 0) {
        toast.success(`Synced ${created} new provider${created > 1 ? "s" : ""} from transactions`);
      } else {
        toast.info("All providers are already synced");
      }
      fetchProviders();
    } catch (e) { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const deleteProvider = async (id, name) => {
    if (!window.confirm(`Delete provider "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/providers/${id}`);
      toast.success(`Deleted "${name}"`);
      fetchProviders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot delete provider");
    }
  };

  useEffect(() => { fetchProviders(); }, []);

  const filtered = useMemo(() => {
    let list = providers;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (sortBy === "spend") {
        const aNet = Math.abs(a.total_income - a.total_spend);
        const bNet = Math.abs(b.total_income - b.total_spend);
        return bNet - aNet;
      }
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "recent") return (b.last_transaction_date || "").localeCompare(a.last_transaction_date || "");
      if (sortBy === "count") return b.transaction_count - a.transaction_count;
      return 0;
    });
  }, [providers, search, sortBy]);

  const totalSpend = useMemo(() => providers.reduce((s, p) => s + p.total_spend, 0), [providers]);
  const totalIncome = useMemo(() => providers.reduce((s, p) => s + p.total_income, 0), [providers]);
  const netPosition = useMemo(() => totalIncome - totalSpend, [totalIncome, totalSpend]);
  const totalUpcoming = useMemo(() => providers.reduce((s, p) => s + p.upcoming_total, 0), [providers]);
  const activeProviders = useMemo(() => providers.filter(p => p.transaction_count > 0).length, [providers]);
  const supplierCount = useMemo(() => providers.filter(p => p.total_spend > p.total_income).length, [providers]);
  const customerCount = useMemo(() => providers.filter(p => p.total_income > p.total_spend).length, [providers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div data-testid="provider-directory" className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-stone-900 tracking-tight">Provider Directory</h1>
        <p className="text-sm text-stone-500 mt-1">Track and manage all your business providers and vendor relationships</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-stone-200/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-stone-400" />
              <span className="text-xs text-stone-500">Total Providers</span>
            </div>
            <p data-testid="total-providers" className="text-xl font-bold text-stone-900">{providers.length}</p>
            <p className="text-[11px] text-stone-400">{supplierCount} suppliers, {customerCount} customers</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-stone-400" />
              <span className="text-xs text-stone-500">Net Position</span>
            </div>
            <p data-testid="net-position" className={`text-xl font-bold ${netPosition >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {netPosition >= 0 ? "+" : "-"}{fmt(Math.abs(netPosition))}
            </p>
            <p className="text-[11px] text-stone-400">{fmt(totalIncome)} in / {fmt(totalSpend)} out</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-stone-500">Upcoming Payments</span>
            </div>
            <p data-testid="upcoming-payments" className="text-xl font-bold text-amber-600">{fmt(totalUpcoming)}</p>
            <p className="text-[11px] text-stone-400">scheduled</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="w-4 h-4 text-stone-400" />
              <span className="text-xs text-stone-500">Active Providers</span>
            </div>
            <p className="text-xl font-bold text-stone-900">{activeProviders}</p>
            <p className="text-[11px] text-stone-400">with transactions</p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            data-testid="provider-search"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search providers..."
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="sync-providers-btn" variant="outline" size="sm" onClick={syncProviders} disabled={syncing}
            className="h-9 text-xs whitespace-nowrap">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Sync from Transactions
          </Button>
          <ArrowUpDown className="w-4 h-4 text-stone-400" />
          <select
            data-testid="provider-sort"
            value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="spend">Highest Net Position</option>
            <option value="name">Name (A-Z)</option>
            <option value="recent">Most Recent</option>
            <option value="count">Most Transactions</option>
          </select>
        </div>
      </div>

      {/* Provider List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
          <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700 mb-1">
            {search ? "No providers found" : "No providers yet"}
          </h3>
          <p className="text-sm text-stone-500">
            {search ? "Try a different search term" : "Providers are automatically added when you enter a vendor name on transactions"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => <ProviderCard key={p.id} provider={p} onUpdate={fetchProviders} onDelete={deleteProvider} />)}
        </div>
      )}
    </div>
  );
}
